/**
 * Settings → Recordings and privacy (prototype VIEWS.settings `else b=`, docs/prototype.html L4436), as adjusted by
 * step-8 decision #8 (docs/architecture/step8-contract-decisions.md):
 *  - "Ask for “participants informed” before audio upload" — recordings.require_consent (settings.write);
 *  - "Keep meeting audio for" 30 / 90 / 180 days / Forever (default Forever) — a system setting: only the
 *    settings.system holder (the founder) changes it, the associate director sees it read-only;
 *  - the consent text people confirm on "Log a meeting", generated by the server from the retention setting;
 *  - "Show the tone label to members" — tone.show_label_to_members (decision #13: the label only, never the reason);
 *  - the prototype's note on who sees audio.
 * Each change saves at once (the prototype's switches do), with the shared config_version as If-Match.
 */
import { useState } from 'react';
import type { PatchPrivacySettingsBody, PrivacySettings } from '@amplispot/contracts';
import { ApiFailure } from '../../api';
import { useSession } from '../../lib/session';
import { usePrivacy } from '../../lib/meetings';
import { RETENTION_OPTIONS, useUpdatePrivacy } from '../../lib/google';
import { ErrorState, Loading, Modal, Switch, errorText, useToast } from '../../ui/kit';

type Retention = PrivacySettings['recordings']['retention_days'];
const retentionLabel = (d: Retention) => RETENTION_OPTIONS.find(([v]) => v === d)?.[1] ?? 'Forever';
/** Forever (null) is the longest; anything else compares by days. */
const shorter = (next: Retention, cur: Retention) => next !== null && (cur === null || next < cur);

export function RecordingsPrivacy() {
  const { can } = useSession();
  const q = usePrivacy();
  const save = useUpdatePrivacy();
  const toast = useToast();
  const [confirm, setConfirm] = useState<Retention | undefined>(undefined);
  const system = can('settings.system');

  if (q.isPending) return <div className="panel"><Loading /></div>;
  if (q.isError) return <div className="panel"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></div>;
  const p = q.data;

  const patch = (body: PatchPrivacySettingsBody, done: string) =>
    save.mutate(
      { version: p.config_version, body },
      {
        onSuccess: () => toast(done),
        onError: (e) => {
          if (e instanceof ApiFailure && e.status === 412) {
            void q.refetch();
            toast('Someone else changed Settings just now. This page now shows their version: check it and try again.');
          } else toast(errorText(e));
        },
      },
    );

  const pickRetention = (d: Retention) => {
    if (d === p.recordings.retention_days) return;
    if (shorter(d, p.recordings.retention_days)) return setConfirm(d);
    patch({ recordings: { retention_days: d } }, `Saved. Meeting audio is now kept ${d === null ? 'until an administrator deletes it' : `for ${retentionLabel(d)}`}.`);
  };

  return (
    <div className="panel">
      <div className="bd" style={{ display: 'grid', gap: 20, maxWidth: 620 }}>
        <div className="split">
          <div className="grow">
            <b>Ask for “participants informed” before audio upload</b>
          </div>
          <Switch
            on={p.recordings.require_consent}
            disabled={save.isPending}
            label="Ask for participants informed before audio upload"
            onClick={() =>
              patch(
                { recordings: { require_consent: !p.recordings.require_consent } },
                p.recordings.require_consent ? 'Saved. Audio uploads no longer ask for the confirmation.' : 'Saved. Audio uploads now ask everyone to confirm the call knew it was recorded.',
              )
            }
          />
        </div>

        <div>
          <b>Keep meeting audio for</b>
          <select
            className="in"
            style={{ marginTop: 6, maxWidth: 220 }}
            value={p.recordings.retention_days === null ? 'forever' : String(p.recordings.retention_days)}
            disabled={!system || save.isPending}
            aria-label="Keep meeting audio for"
            onChange={(e) => pickRetention(e.target.value === 'forever' ? null : (Number(e.target.value) as 30 | 90 | 180))}
          >
            {RETENTION_OPTIONS.map(([v, label]) => (
              <option key={label} value={v === null ? 'forever' : String(v)}>
                {label}
              </option>
            ))}
          </select>
          {!system ? (
            <div className="sub" style={{ color: 'var(--sub)', fontSize: 12.5, marginTop: 4 }}>
              Only the founder changes how long audio is kept.
            </div>
          ) : null}
          <div className="sub" style={{ color: 'var(--sub)', fontSize: 12.5, marginTop: 8 }}>
            {p.recordings.require_consent ? 'What people confirm before they upload audio:' : 'The confirmation, when it is asked for:'}
          </div>
          <div style={{ fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>“{p.recordings.consent_text}”</div>
        </div>

        <div className="split">
          <div className="grow">
            <b>Show the tone label to members</b>
            <div className="sub" style={{ color: 'var(--sub)', fontSize: 12.5 }}>
              Ops members see the label on their clients’ emails, such as “Unhappy”. The reason and the quote stay with the Ops associate director and founders.
            </div>
          </div>
          <Switch
            on={p.tone.show_label_to_members}
            disabled={save.isPending}
            label="Show the tone label to members"
            onClick={() =>
              patch(
                { tone: { show_label_to_members: !p.tone.show_label_to_members } },
                p.tone.show_label_to_members ? 'Saved. Members no longer see the tone label.' : 'Saved. Members now see the tone label on their clients’ emails.',
              )
            }
          />
        </div>

        <div className="note">
          Audio is visible only to the person who added it, the Ops associate director and founders. Revenue is hidden from Ops members at the data level, not just on screen.
        </div>
        {/* ADR-001 open question 1 (may client audio leave India) is still open; the prototype says so here. */}
        <div className="note warn">Where BFSI client data is stored is still an open decision for your tech team.</div>
      </div>

      {confirm !== undefined ? (
        <Modal
          title={`Keep meeting audio for ${retentionLabel(confirm)}?`}
          sub="Audio older than this is deleted at the next nightly run, and those meetings can no longer be transcribed again. The meetings and their minutes stay."
          onClose={() => setConfirm(undefined)}
          footer={
            <>
              <div className="grow" />
              <button className="btn" onClick={() => setConfirm(undefined)}>
                Cancel
              </button>
              <button
                className="btn pri"
                disabled={save.isPending}
                onClick={() => {
                  const d = confirm;
                  setConfirm(undefined);
                  patch({ recordings: { retention_days: d } }, `Saved. Meeting audio is now kept for ${retentionLabel(d)}.`);
                }}
              >
                Keep {retentionLabel(confirm)}
              </button>
            </>
          }
        >
          {null}
        </Modal>
      ) : null}
    </div>
  );
}
